/**
 * Helper functions for pattern and color operations
 */

const SLOT_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];



/**
 * Determine the texture type for a given warranty ID based on all its occurrences.
 * If an ID has both CL and L, it's "Tip Shear/Loop".
 * If an ID has CL and C, or C and L, it's "Tip Shear".
 * CL alone = "Tip Shear", L alone = "Loop", C alone = "Cut".
 * 
 * @param {string} id - The warranty ID to check
 * @param {string[]} warrantyIds - All warranty IDs (may contain duplicates)
 * @param {string[]} textureTypes - Corresponding texture types for each warranty position
 * @returns {string} The resolved texture label
 */
export function getTextureTypeForId(id, warrantyIds, textureTypes) {
    const codes = new Set();
    warrantyIds.forEach((wId, idx) => {
        if (wId === id) codes.add(textureTypes[idx]);
    });

    if (codes.has('CL') && codes.has('L')) return 'Tip Shear/Loop';
    if (codes.has('CL') && codes.has('C')) return 'Tip Shear';
    if (codes.has('CL')) return 'Tip Shear';
    if (codes.has('C') && codes.has('L')) return 'Tip Shear';
    if (codes.has('L')) return 'Loop';
    if (codes.has('C')) return 'Cut';
    return 'Cut';
}

/**
 * Build sel string and cut/loop string based on pattern style ordering
 * 
 * Style represents the order of sel strings in warranty after duplicates are removed.
 * 
 * Example:
 *   style: "2,1,2,3,1"
 *   defaultRoomSceneSpecifier: "C,C,L,C,L"
 *   warranty: "5917115, 5917114, 5917115, 5917125, 5917114"
 * 
 * Step 1: Build mapper from warranty to texture type
 *   { 5917115: 'C', 5917114: 'C', 5917125: 'L' }
 * 
 * Step 2: Remove duplicates from warranty
 *   unique warranty: [5917115, 5917114, 5917125]
 * 
 * Step 3: Map style positions (1-based) to unique warranty
 *   style 2 -> 5917114, style 1 -> 5917115, etc.
 * 
 * Output:
 *   selString: "!5917114,!5917115,!5917114,!5917125,!5917115"
 *   cutLoopString: "@CUT,@CUT,@CUT,@LOOP,@CUT"
 * 
 * @param {Object} chosenPattern - The pattern object with style, warranty, and defaultRoomSceneSpecifier
 * @returns {Object} { selString, cutLoopString } or { selString: '', cutLoopString: '' } if invalid
 */
export function buildSelAndCutLoopStrings(chosenPattern) {
    if (!chosenPattern || !chosenPattern.warranty || !chosenPattern.defaultRoomSceneSpecifier) {
        return { selString: '', rawSelString: '', cutLoopString: '' };
    }

    // Parse warranty IDs
    const warrantyIds = chosenPattern.warranty
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

    // Parse texture types from defaultRoomSceneSpecifier
    const textureTypes = chosenPattern.defaultRoomSceneSpecifier
        .split(',')
        .map(t => t.trim());

    // Build raw sel string without ! prefix
    const rawSelString = warrantyIds.join(',');

    // Build sel string with ! prefix
    const selString = warrantyIds
        .map(id => `!${id}`)
        .join(',');

    // Build cut/loop string - texture types correspond directly to warranty positions
    // L = Loop, C = Cut, CL = Cut Loop
    const cutLoopString = textureTypes
        .map(type => {
            switch (type) {
                case 'L':
                    return '@LOOP';
                case 'CL':
                    return '@CUTLOOP';
                case 'C':
                default:
                    return '@CUT';
            }
        })
        .join(',');

    return { selString, rawSelString, cutLoopString };
}


/**
 * Generate swatch URL using patternColors currentId values
 * 
 * @param {Object} chosenPattern - The pattern object with specifier and defaultRoomSceneSpecifier
 * @param {Array} patternColors - Array of color objects with currentId
 * @param {number} size - Image size (default: 1024)
 * @returns {string|null} The swatch URL or null if invalid inputs
 */
export function getSwatchUrl(chosenPattern, patternColors, size = 1024) {
    if (!chosenPattern || !patternColors || patternColors.length === 0) return null;

    const defaultRoomSceneSpecifier = chosenPattern.defaultRoomSceneSpecifier;
    if (!defaultRoomSceneSpecifier) return null;

    // Get the cutLoopString from the pattern
    const output = buildSelAndCutLoopStrings(chosenPattern);
    
    // Build sel string directly from patternColors (already in correct order)
    const selString = patternColors
        .map(color => `!${color.currentId}`)
        .join(',');

    return `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${output.cutLoopString})),FOCUS=(@SIZE[1]/(@PATRES[1]|@RES[1])))&size=${size}&extra=(SIZE=(?,(@SIZE[1]*@PATRES[2]/@PATRES[1])))`;
}

export { SLOT_LABELS };
